// パフォーマー詳細(設計書 §3.5.3)
// 基本情報 + 現在の配信 + plan / 表示名 / 有効無効 / 削除 + モザイク設定リンク

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiClientError } from '../../api/client';
import {
  PERFORMER_STATUS_LABELS,
  useApprovePerformer,
  useDeletePerformer,
  useDisablePerformer,
  useEnablePerformer,
  usePerformerDetail,
  useRejectPerformer,
  useUpdatePerformer,
  type PerformerDetail,
  type Plan,
} from '../../api/admin/performers';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { MosaicSettingsForm } from './MosaicSettingsForm';

export function AdminPerformerDetailPage() {
  const { ulid = '' } = useParams<{ ulid: string }>();
  const navigate = useNavigate();
  const detailQuery = usePerformerDetail(ulid || null);

  const [editingProfile, setEditingProfile] = useState(false);
  const [editingMosaic, setEditingMosaic] = useState(false);
  const [disablingReason, setDisablingReason] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const approveMutation = useApprovePerformer();
  const rejectMutation = useRejectPerformer();
  const enableMutation = useEnablePerformer();
  const disableMutation = useDisablePerformer();
  const deleteMutation = useDeletePerformer();

  return (
    <AdminLayout
      title="パフォーマー詳細"
      description={detailQuery.data ? detailQuery.data.email : undefined}
      actions={
        <Link to="/admin/performers" style={{ fontSize: '11px' }}>
          ← 一覧へ
        </Link>
      }
    >
      {detailQuery.isLoading && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {detailQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {detailQuery.error instanceof ApiClientError
              ? detailQuery.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {detailQuery.data && (
        <>
          <BasicInfoSection
            performer={detailQuery.data}
            onEdit={() => setEditingProfile(true)}
          />

          {detailQuery.data.current_session && (
            <Card title="現在の配信">
              <dl
                style={{
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: '120px 1fr',
                  rowGap: 6,
                  fontSize: '11px',
                }}
              >
                <Term>セッション</Term>
                <Detail>{detailQuery.data.current_session.ulid}</Detail>
                <Term>サーバー</Term>
                <Detail>
                  {detailQuery.data.current_session.server_name} (slot{' '}
                  {detailQuery.data.current_session.slot})
                </Detail>
                <Term>開始時刻</Term>
                <Detail>{formatDateTime(detailQuery.data.current_session.started_at)}</Detail>
              </dl>
            </Card>
          )}

          <Card
            title="モザイク設定"
            description="検出対象 / 出力解像度 / 学習データディレクトリ等"
            footer={
              <div style={{ maxWidth: 220 }}>
                <Button variant="secondary" onClick={() => setEditingMosaic(true)}>
                  モザイク設定を編集
                </Button>
              </div>
            }
          >
            {detailQuery.data.mosaic_settings ? (
              <MosaicSummary settings={detailQuery.data.mosaic_settings} />
            ) : (
              <p style={{ margin: 0, color: 'var(--color-text-tertiary)', fontSize: '11px' }}>
                モザイク設定が初期化されていません。
              </p>
            )}
          </Card>

          <Card title="状態管理">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {detailQuery.data.status === 'pending' && (
                <>
                  <Action label="承認" onClick={() => approveMutation.mutate(ulid)} pending={approveMutation.isPending} />
                  <Action label="却下" variant="danger" onClick={() => rejectMutation.mutate(ulid)} pending={rejectMutation.isPending} />
                </>
              )}
              {detailQuery.data.status === 'active' && (
                <Action
                  label="無効化"
                  variant="danger"
                  onClick={() => setDisablingReason('')}
                  pending={false}
                />
              )}
              {detailQuery.data.status === 'disabled' && (
                <Action label="有効化" onClick={() => enableMutation.mutate(ulid)} pending={enableMutation.isPending} />
              )}
              <Action
                label="アカウントを削除"
                variant="danger"
                onClick={() => setConfirmDelete(true)}
                pending={false}
              />
            </div>
            {(approveMutation.error ||
              rejectMutation.error ||
              enableMutation.error ||
              disableMutation.error) && (
              <p style={{ margin: '12px 0 0', fontSize: '11px', color: 'var(--color-error-text)' }}>
                {extractMessage(
                  approveMutation.error ?? rejectMutation.error ?? enableMutation.error ?? disableMutation.error,
                )}
              </p>
            )}
            {detailQuery.data.status === 'disabled' && detailQuery.data.disabled_reason && (
              <p style={{ margin: '12px 0 0', fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
                無効化理由: {detailQuery.data.disabled_reason}
              </p>
            )}
          </Card>
        </>
      )}

      <Modal
        open={editingProfile && detailQuery.data !== undefined}
        title="プロフィール編集"
        onClose={() => setEditingProfile(false)}
      >
        {detailQuery.data && (
          <ProfileEditForm
            performer={detailQuery.data}
            onClose={() => setEditingProfile(false)}
          />
        )}
      </Modal>

      <Modal
        open={editingMosaic && detailQuery.data?.mosaic_settings !== undefined}
        title="モザイク設定"
        onClose={() => setEditingMosaic(false)}
        width={640}
      >
        {detailQuery.data?.mosaic_settings && (
          <MosaicSettingsForm
            ulid={ulid}
            initial={detailQuery.data.mosaic_settings}
            onClose={() => setEditingMosaic(false)}
          />
        )}
      </Modal>

      <Modal
        open={disablingReason !== null}
        title="アカウントを無効化"
        onClose={() => setDisablingReason(null)}
        footer={
          <>
            <div style={{ minWidth: 100 }}>
              <Button variant="secondary" onClick={() => setDisablingReason(null)}>
                キャンセル
              </Button>
            </div>
            <div style={{ minWidth: 110 }}>
              <Button
                variant="danger"
                disabled={disableMutation.isPending || !disablingReason?.trim()}
                onClick={async () => {
                  if (!disablingReason?.trim()) return;
                  try {
                    await disableMutation.mutateAsync({
                      ulid,
                      reason: disablingReason.trim(),
                    });
                    setDisablingReason(null);
                  } catch {
                    // 親で表示
                  }
                }}
              >
                {disableMutation.isPending ? '実行中…' : '無効化'}
              </Button>
            </div>
          </>
        }
      >
        <Input
          label="理由(必須、500 文字以内)"
          value={disablingReason ?? ''}
          maxLength={500}
          onChange={(e) => setDisablingReason(e.target.value)}
        />
      </Modal>

      <Modal
        open={confirmDelete}
        title="アカウントを削除しますか?"
        onClose={() => setConfirmDelete(false)}
        width={420}
        footer={
          <>
            <div style={{ minWidth: 100 }}>
              <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                キャンセル
              </Button>
            </div>
            <div style={{ minWidth: 110 }}>
              <Button
                variant="danger"
                disabled={deleteMutation.isPending}
                onClick={async () => {
                  try {
                    await deleteMutation.mutateAsync(ulid);
                    navigate('/admin/performers', { replace: true });
                  } catch {
                    // エラーは下で表示
                  }
                }}
              >
                {deleteMutation.isPending ? '削除中…' : '削除する'}
              </Button>
            </div>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '12px' }}>
          論理削除されます(物理削除ではないため、後から復旧は可能です)。
        </p>
        {deleteMutation.error && (
          <p style={{ margin: '12px 0 0', fontSize: '11px', color: 'var(--color-error-text)' }}>
            {extractMessage(deleteMutation.error)}
          </p>
        )}
      </Modal>
    </AdminLayout>
  );
}

function BasicInfoSection({
  performer,
  onEdit,
}: {
  performer: PerformerDetail;
  onEdit: () => void;
}) {
  return (
    <Card
      title="基本情報"
      footer={
        <div style={{ maxWidth: 200 }}>
          <Button variant="secondary" onClick={onEdit}>
            プロフィール編集
          </Button>
        </div>
      }
    >
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: '140px 1fr',
          rowGap: 8,
          fontSize: '11px',
        }}
      >
        <Term>表示名</Term>
        <Detail style={{ fontSize: '13px', color: 'var(--color-text-primary)' }}>
          {performer.display_name}
        </Detail>
        <Term>メール</Term>
        <Detail>{performer.email}</Detail>
        <Term>プラン</Term>
        <Detail style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {performer.plan}
        </Detail>
        <Term>ステータス</Term>
        <Detail>{PERFORMER_STATUS_LABELS[performer.status]}</Detail>
        <Term>Google 連携</Term>
        <Detail>
          {performer.google_linked ? (
            <span style={{ color: 'var(--color-success-text)' }}>連携済み</span>
          ) : (
            '未連携'
          )}
        </Detail>
        <Term>承認</Term>
        <Detail>
          {performer.approved_at
            ? `${formatDateTime(performer.approved_at)} (${performer.approved_by_admin?.name ?? ''})`
            : '未承認'}
        </Detail>
        <Term>累計配信</Term>
        <Detail>
          {formatDuration(performer.stats.total_streaming_seconds)} ・{' '}
          {performer.stats.total_session_count} セッション
        </Detail>
        <Term>登録日</Term>
        <Detail>{formatDateTime(performer.created_at)}</Detail>
        <Term>最終ログイン</Term>
        <Detail>
          {performer.last_login_at ? formatDateTime(performer.last_login_at) : '—'}
        </Detail>
      </dl>
    </Card>
  );
}

function ProfileEditForm({
  performer,
  onClose,
}: {
  performer: PerformerDetail;
  onClose: () => void;
}) {
  const updateMutation = useUpdatePerformer();
  const [displayName, setDisplayName] = useState(performer.display_name);
  const [plan, setPlan] = useState<Plan>(performer.plan);

  const dirty =
    displayName.trim() !== performer.display_name || plan !== performer.plan;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty) {
      onClose();
      return;
    }
    const body: { display_name?: string; plan?: Plan } = {};
    if (displayName.trim() !== performer.display_name) {
      body.display_name = displayName.trim();
    }
    if (plan !== performer.plan) body.plan = plan;
    try {
      await updateMutation.mutateAsync({ ulid: performer.ulid, body });
      onClose();
    } catch {
      // 下で表示
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Input
        label="表示名"
        value={displayName}
        maxLength={100}
        onChange={(e) => setDisplayName(e.target.value)}
      />
      <Select
        label="プラン"
        value={plan}
        onChange={(e) => setPlan(e.target.value as Plan)}
      >
        <option value="free">Free</option>
        <option value="pro">Pro</option>
      </Select>
      {updateMutation.error && (
        <p style={{ margin: '0 0 12px', fontSize: '11px', color: 'var(--color-error-text)' }}>
          {extractMessage(updateMutation.error)}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{ minWidth: 100 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
        </div>
        <div style={{ minWidth: 100 }}>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>
    </form>
  );
}

function MosaicSummary({ settings }: { settings: PerformerDetail['mosaic_settings'] & {} }) {
  return (
    <dl
      style={{
        margin: 0,
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        rowGap: 6,
        fontSize: '11px',
      }}
    >
      <Term>検出対象</Term>
      <Detail>
        {[
          settings.mosaic_target_genital ? '性器' : null,
          settings.mosaic_target_breast ? '胸部' : null,
        ]
          .filter(Boolean)
          .join(' / ') || '(無効)'}
      </Detail>
      <Term>検出モデル</Term>
      <Detail>{settings.detection_model}</Detail>
      <Term>学習データディレクトリ</Term>
      <Detail style={{ fontFamily: 'monospace', fontSize: '10.5px' }}>
        {settings.training_data_dir ?? '—'}
      </Detail>
      <Term>出力解像度</Term>
      <Detail>
        {settings.output_width}×{settings.output_height} @ {settings.output_framerate} fps
      </Detail>
      <Term>出力ビットレート</Term>
      <Detail>{settings.output_bitrate_kbps.toLocaleString()} kbps</Detail>
      <Term>出力品質</Term>
      <Detail>{settings.output_quality}</Detail>
      <Term>出力先 URL 上書き</Term>
      <Detail style={{ fontFamily: 'monospace', fontSize: '10.5px' }}>
        {settings.output_url_override ?? '—'}
      </Detail>
      <Term>ストリームキー上書き</Term>
      <Detail>
        {settings.stream_key_override_set ? '設定済み(値は表示されません)' : '—'}
      </Detail>
    </dl>
  );
}

function Action({
  label,
  onClick,
  variant = 'secondary',
  pending,
}: {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  pending: boolean;
}) {
  return (
    <div style={{ minWidth: 120 }}>
      <Button
        variant={variant}
        onClick={onClick}
        disabled={pending}
        style={{ padding: '6px 10px', fontSize: '11px' }}
      >
        {pending ? '実行中…' : label}
      </Button>
    </div>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return (
    <dt style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>{children}</dt>
  );
}

function Detail({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <dd
      style={{
        margin: 0,
        fontSize: '11px',
        color: 'var(--color-text-secondary)',
        wordBreak: 'break-all',
        ...style,
      }}
    >
      {children}
    </dd>
  );
}

function extractMessage(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  return '処理に失敗しました';
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatDuration(seconds: number): string {
  if (!seconds) return '0';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h} 時間 ${m} 分`;
  return `${m} 分`;
}
